import { Notice, NoticeTypes, Shadow, Text, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { RouteProp, useRoute } from '@react-navigation/native';
import { FlatList } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';

import { SignedInRootStackParamList } from '../../../../../../../../routes/types';
import { fieldsTransform } from '../../../../../../../../store/batchTicket/normalize';
import { useBatchTicketResumeDetail } from '../../../../../../hooks/batchTicketResumeDetail';
import { flatListProps } from '../../../../../../utils';
import { AddedProductItemData, AddedProductsTypes } from '../../../../../../utils/types';
import { AddedProductItemSection } from '../../../../../Cart/components/AddedProductItemSection';
import { Empty } from '../../../../../Cart/components/Empty';
import { ItemSeparator } from '../../../../../ItemSeparator';
import { ProveBasketOrigin } from '../ProveBasketOrigin';
import { ResumeDetailsInfo } from '../ResumeDetailsInfo';

export interface ResumeDetailProps {
  customerName: string;
  customerAccount: string;
  suitabilityProfile: string;
  captionIcons: string[];
  assetsAdded: number;
  assetsInvalids: number;
  availableBalance: number;
  availableBalanceFormatted: string;
  allocatedBalance: number;
  allocatedBalanceFormatted: string;
  remainingBalance: number;
  remainingBalanceFormatted: string;
  allocatedPercentage: number;
  allocatedPercentageFormatted: string;
  remainingPercentage: number;
  remainingPercentageFormatted: string;
}

interface AddedItemsProps {
  addedItemsIsEmpty: boolean;
  itemsAddedData: AddedProductItemData[];
  noticeProps: NoticeTypes.Props;
}

export const AddedItems: React.FC<AddedItemsProps> = ({
  addedItemsIsEmpty,
  itemsAddedData,
  noticeProps,
}) => {
  const marketTypesEntries = useMemo(
    () => Object.entries(fieldsTransform) as [keyof typeof fieldsTransform, AddedProductsTypes][],
    []
  );
  const currentMarketTypes = useMemo(
    () => itemsAddedData.filter(({ items }) => !!items.length).map(({ type }) => type),
    [itemsAddedData]
  );
  const [requireBasketOrigin, setRequireBasketOrigin] = useState(false);
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'BatchTicketResumeDetail'>>();
  const { basketCustomerOrderItem } = useBatchTicketResumeDetail();

  useEffect(() => {
    const customerOffersFiltered = basketCustomerOrderItem?.customerOffers?.filter((offer) => {
      const marketType = marketTypesEntries.find(([value]) => value === offer?.marketType);
      return marketType ? currentMarketTypes.includes(marketType[1]) : false;
    });
    if (customerOffersFiltered) {
      const includesRequiredOriginError = !!customerOffersFiltered?.some((offer) =>
        offer?.basketOffers?.some((basketOffer) =>
          basketOffer?.errors?.some(({ errorCode }) => errorCode === 'order_origin_required')
        )
      );
      setRequireBasketOrigin(includesRequiredOriginError);
    }
  }, [basketCustomerOrderItem, currentMarketTypes, marketTypesEntries]);

  if (addedItemsIsEmpty) {
    return (
      <Empty
        description="A lista esta vazia, Este item sera removido desta solicitação."
        title="Você não possui itens adicionados"
      />
    );
  }
  return (
    <VStack
      mt={Spacing['stack.xxxs']}
      space={Spacing['stack.xxxs']}
      flex={1}
      px={{ base: Spacing['inline.xxxs'], sm: Spacing['inline.xxs'] }}>
      <ResumeDetailsInfo />
      <Notice {...noticeProps} />
      {!!requireBasketOrigin && <ProveBasketOrigin />}
      <Text variant="bold" color="neutral.dark" fontSize={Typography['fontsize.md']}>
        Itens adicionados
      </Text>
      <Shadow level={3} flex={1}>
        <FlatList
          {...flatListProps}
          data={itemsAddedData}
          keyExtractor={(item) => item.type}
          maxH="full"
          flex={1}
          ItemSeparatorComponent={ItemSeparator}
          renderItem={({ item }) => (
            <AddedProductItemSection
              // hasPercentage
              itemData={item}
              navigationType={params.type}
            />
          )}
        />
      </Shadow>
    </VStack>
  );
};
